// Build a human-friendly current-position label from entitlements + progress.
// Examples: "A → B", "Phase 1 · 60%", "Builder Phase · Active", "Genesis · A–Z access"
//
// Pure JS, no React deps. Output is short mono-cap-friendly.

import { phaseForLetter } from "@/lib/portal/phaseGrouping";

const LETTERS_AH = ["A", "B", "C", "D", "E", "F", "G", "H"];

export function buildPathLabel({ user, progress }) {
    const ent = (user && user.entitlements) || {};
    const tier = ent.tier;
    const unlocked = ent.unlocked_letters || [];
    const isGenesis = tier === "genesis" || !!ent.genesis_iz_early_access;
    const modules = progress?.modules || {};
    const lastActive = progress?.last_active_module;

    // Genesis special label
    if (isGenesis && unlocked.length >= 20) {
        return "Genesis · A–Z access";
    }

    // Determine current cursor letter
    const cursor =
        lastActive ||
        // first unlocked letter not yet completed
        LETTERS_AH.find((L) => unlocked.includes(L) && !modules[L]?.completed_at) ||
        unlocked[0] ||
        null;

    if (!cursor) return "Awaiting first letter";

    const cursorIdx = LETTERS_AH.indexOf(cursor);
    const nextLetter = cursorIdx >= 0 && cursorIdx + 1 < LETTERS_AH.length ? LETTERS_AH[cursorIdx + 1] : null;

    const isCurrentDone = modules[cursor]?.completed_at;
    if (isCurrentDone && nextLetter && unlocked.includes(nextLetter)) {
        return `${cursor} → ${nextLetter}`;
    }
    if (isCurrentDone) {
        const phase = phaseForLetter(cursor);
        return phase ? `${phase.name} Phase · Active` : `${cursor} · Complete`;
    }

    // Mid-progress
    const lessons = modules[cursor]?.lessons || {};
    const done = Object.values(lessons).filter(Boolean).length;
    if (done > 0) {
        const pct = Math.min(100, Math.round((done / 3) * 100));
        return `${cursor} · ${pct}%`;
    }

    // Just unlocked
    if (nextLetter && unlocked.includes(nextLetter)) {
        return `${cursor} → ${nextLetter}`;
    }
    return `${cursor} · ready`;
}

export function accessStateLabel(user) {
    const tier = (user?.entitlements?.tier || "").toLowerCase();
    if (!tier) return "PUBLIC";
    return `${tier.toUpperCase()} ACTIVE`;
}

export function displayName(user) {
    if (!user) return "Operator";
    if (user.first_name) return user.first_name;
    if (user.name) return user.name.split(" ")[0];
    const local = (user.email || "").split("@")[0] || "Operator";
    // Title-case the first letter
    return local.charAt(0).toUpperCase() + local.slice(1);
}
